import React, { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Stack } from '@atlaskit/primitives/compiled';

import type {
	ITFGAnalyticsCallbackParams,
	Edition as InTrialFeatureGatesBannerEdition,
} from '@atlassian/in-trial-feature-gates-banner';
import {
	FEATURE_ID_USD_MODAL,
	formatEventCallBackPayloadForAtlasKitAnalyticsEvent,
} from '@atlassian/in-trial-feature-gates-banner';

import { useInTrialFeatureGatesExperimentEnrollmentAB } from '@confluence/in-trial-feature-gates-banner/entry-points/useInTrialFeatureGatesExperimentEnrollmentAB';
import { InTrialFeatureGatesBannerWithData } from '@confluence/in-trial-feature-gates-banner/entry-points/InTrialFeatureGatesBannerWithData';
import { NavigationLockout } from '@confluence/navigation-lockout';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery';

import { GeneralAccess, type GeneralAccessProps } from './generalAccess/GeneralAccess';
import { PrincipalsStager, type PrincipalsStagerProps } from './PrincipalsStager';
import { TitleAndButtons, type TitleAndButtonsProps } from './TitleAndButtons';
import { InfoMessage, type InfoMessageProps } from './infoMessage/InfoMessage';
import { SpecificAccess, type SpecificAccessProps } from './specificAccess/SpecificAccess';

export type HeaderProps = {
	titleAndButtons: TitleAndButtonsProps;
	generalAccess?: GeneralAccessProps;
	principalsStager?: PrincipalsStagerProps;
	infoMessage?: InfoMessageProps;
	specificAccess?: SpecificAccessProps;
	hasNavigationLockout: boolean;
	onSuggestedUserClick?: () => void;
	// showDraftMessage: boolean; // TODO
};

export const Header = ({
	titleAndButtons,
	generalAccess,
	specificAccess,
	principalsStager,
	infoMessage,
	hasNavigationLockout,
	onSuggestedUserClick,
	// showDraftMessage,
}: HeaderProps) => {
	const { cloudId, edition } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const inTrialFeatureGatesAnalyticsCallback = useCallback(
		(params: ITFGAnalyticsCallbackParams) => {
			createAnalyticsEvent(formatEventCallBackPayloadForAtlasKitAnalyticsEvent(params)).fire();
		},
		[createAnalyticsEvent],
	);
	// AB hooks for in trial feature gates experiment - https://hello.atlassian.net/wiki/spaces/Land/pages/4343595130/Project+poster+Confluence+Feature+Gate+Add+Payment+Prompts
	const { canRenderTreatment } = useInTrialFeatureGatesExperimentEnrollmentAB({
		featureId: FEATURE_ID_USD_MODAL,
		tenantId: cloudId,
		sendOperationalEvent: inTrialFeatureGatesAnalyticsCallback,
		sendTrackEvent: inTrialFeatureGatesAnalyticsCallback,
		skip: edition === ConfluenceEdition.FREE,
		extraAnalyticsAttributes: {
			edition: edition?.toLowerCase(),
		},
	});

	return (
		<Stack grow="fill" space="space.200">
			<TitleAndButtons {...titleAndButtons} />
			{principalsStager && (
				<PrincipalsStager {...principalsStager} onSuggestedUserClick={onSuggestedUserClick} />
			)}
			{infoMessage && <InfoMessage {...infoMessage} />}
			{canRenderTreatment && edition && (
				<InTrialFeatureGatesBannerWithData
					featureId={FEATURE_ID_USD_MODAL}
					edition={edition.toLowerCase() as InTrialFeatureGatesBannerEdition}
					tenantId={cloudId}
					sendOperationalEvent={inTrialFeatureGatesAnalyticsCallback}
					sendTrackEvent={inTrialFeatureGatesAnalyticsCallback}
					sendScreenEvent={inTrialFeatureGatesAnalyticsCallback}
				/>
			)}
			{generalAccess && (
				<GeneralAccess {...generalAccess} showBottomPadding={!Boolean(specificAccess)} />
			)}
			{specificAccess && <SpecificAccess {...specificAccess} />}
			{hasNavigationLockout && <NavigationLockout />}
		</Stack>
	);
};
